import { AppstoreOutlined, BarsOutlined, PictureFilled } from '@ant-design/icons';
import { Card, Empty, Segmented, Space } from 'antd';
import { useMemo } from 'react';
import AC from '../../../components/AccessControl';
import cmsFactory from '../../../components/cmsFactory';
import GridView from '../../../components/contentDisplay/GridView';
import ListView from '../../../components/contentDisplay/ListView';
import ThumbnailView from '../../../components/contentDisplay/ThumbnailView';
import SectionHeader from '../../../components/panels/SectionHeader';
import SideMenu from '../../../components/panels/SideMenu';
import ContentLibraryHeader from '../../../components/navigation/ContentLibraryHeader';
import filterContentKeywordsHTML from '../../../helpers/filterContentKeywordsHTML';
import htmlStringToReactNodes from '../../../helpers/htmlStringToReactNodes';
import { useHistoryState, useSession } from '../../../lib/hooks';
import { CONTENT_KEYWORD_FILTER_BY_STATE } from '../../../helpers/constants';
const ViewOptions = [{
  label: 'List',
  value: 'list',
  icon: <BarsOutlined />,
  component: ListView
}, {
  label: 'Icon',
  value: 'thumbnail',
  icon: <PictureFilled />,
  component: ThumbnailView
}, {
  label: 'Grid',
  value: 'grid',
  icon: <AppstoreOutlined />,
  component: GridView
}];
const PageIndex = ({
  data: {
    name,
    list,
    menu,
    headerHTML,
    displayTypes,
    showSideMenu
  }
}) => {
  const {
    user,
    access
  } = useSession();
  const isAdmin = access.system;
  const userState = user.state?.abbreviation;
  const filteredList = useMemo(() => {
    if (isAdmin) return list;
    const stateFilter = CONTENT_KEYWORD_FILTER_BY_STATE.find(({
      state
    }) => state.test(userState));
    if (!stateFilter) return list;
    const filterRecursively = item => {
      if (Array.isArray(item.children)) {
        return {
          ...item,
          children: item.children.map(filterRecursively).filter(child => !(child.keywords && stateFilter.keywords.some(keyword => keyword.test(child.keywords)))).filter(child => !Array.isArray(child.children) || child.children.length > 0)
        };
      }
      return item;
    };
    return list.map(filterRecursively).filter(item => !(item.keywords && stateFilter.keywords.some(keyword => keyword.test(item.keywords)))).filter(item => !Array.isArray(item.children) || item.children.length > 0);
  }, [list, isAdmin, userState]);
  const viewOptions = useMemo(() => {
    const types = ViewOptions.filter(v => displayTypes.includes(v.value));
    return types.length > 0 ? types : ViewOptions.slice(-1);
  }, [displayTypes]);
  const [view, updateView] = useHistoryState('pageindex-view', viewOptions.at(-1).value);
  const [activeKeys, setActiveKeys] = useHistoryState('pageindex-keys', filteredList.map(d => d.key));
  const viewComponent = useMemo(() => viewOptions.find(v => v.value === view).component, [view, viewOptions]);
  return <>
      <ContentLibraryHeader title={menu?.title} menu={!menu ? null : <ol>
          {menu.pages.map(({
        type,
        slug,
        key,
        linkName,
        name
      }) => <li key={key}>
                <AC.Link label={linkName || name} href={{
          pathname: `/toolkits/${type}/[slug]`,
          query: {
            slug
          }
        }} />
              </li>)}
          </ol>} data-sentry-element="ContentLibraryHeader" data-sentry-source-file="[slug].jsx" />
      <Card data-sentry-element="Card" data-sentry-source-file="[slug].jsx">
        {!filteredList ? <Empty /> : <div className="page-content">
            <div className="page-main-content">
              <SectionHeader title={<h3>{name}</h3>} extra={viewOptions.length > 1 && <Space size="large">
                      <span>
                        View:{' '}
                        <Segmented options={viewOptions} value={view} onChange={value => updateView(value)} />
                      </span>
                    </Space>} />
              {headerHTML && <div className="page-desc">{htmlStringToReactNodes(filterContentKeywordsHTML(headerHTML, userState, isAdmin))}</div>}
              {viewComponent({
            contentData: filteredList,
            activeKeys: activeKeys,
            setActiveKeys: setActiveKeys
          })}
            </div>
            {showSideMenu && <SideMenu />}
          </div>}
      </Card>
    </>;
};
export const getStaticProps = context => ({
  props: {
    dataURL: `/content/pages/${context.params.slug}.json`
  }
});
export const getStaticPaths = async () => ({
  fallback: false,
  paths: require('../../../public/content/pages-paths.json')
});
export default cmsFactory(PageIndex);